<template>
  <RevCard class="mb-16 flex-1 p-24 md:mb-0 md:mt-16">
    <h2 class="heading-2 mb-8">
      {{ i18n(translations.title) }}
    </h2>
    <div class="text-static-default-low body-1 mb-20">
      {{ i18n(translations.description) }}
    </div>
    <WithSkeletonLoader :is-loading="pending">
      <RevForm
        v-if="additionalContactTypeOptions"
        class="flex items-start gap-8"
        :initial-values
        :name="FORM_NAME"
        :validate
        @submit="handleSubmit"
      >
        <template #default="{ values, errors, hasError }">
          <RevInputText
            id="add-contact-email"
            v-model="values.email"
            autocomplete="email"
            class="min-w-[260px]"
            data-cs-mask
            :disabled="isSubmitting"
            :error="errors.email"
            :icon="IconMail"
            :label="i18n(translations.emailLabel)"
            type="email"
          />
          <RevInputSelect
            id="contact_type_select"
            v-model="values.type"
            class="min-w-[260px]"
            :label="i18n(translations.contactTypeslabel)"
            :options="additionalContactTypeOptions"
          />
          <RevButton
            id="add_contact_button"
            :disabled="hasError || isSubmitting"
            :loading="isSubmitting"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.submitButton) }}
          </RevButton>
        </template>
      </RevForm>
      <div
        v-for="additionalContact in additionalContacts"
        :key="additionalContact.type.code"
        class="mb-20 mt-32"
      >
        <div class="body-1-bold mb-20">
          {{ additionalContact.type.label }}
        </div>
        <div class="body-1 flex items-center justify-between md:w-[500px]">
          <div class="mr-16">{{ additionalContact.email }}</div>
          <RevButtonIcon
            :aria-label="i18n(translations.deleteButton)"
            :icon="IconTrash"
            variant="secondary"
            @click="handleDeleteContact(additionalContact)"
          />
        </div>
      </div>
    </WithSkeletonLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import type { GetProfileViewContactsItem } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { setFormValues } from '@ds/components/Form/Form.actions'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { IconMail } from '@ds/icons/IconMail'
import { IconTrash } from '@ds/icons/IconTrash'

import { REGEXP_VALID_EMAIL } from '~/constants/regex'

import WithSkeletonLoader from '../../../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import translations from './AdditionalContacts.translations'
import type { FormContactValues } from './AdditionalContacts.types'

const FORM_NAME = 'add-seller-contact'

const i18n = useI18n()
const { openSuccessToast, openErrorToast } = useTheToast()

const [
  {
    data: contacts,
    error: contactsError,
    pending: contactsPending,
    refresh: contactsRefresh,
  },
  {
    data: contactTypes,
    error: contactTypesError,
    pending: contactTypesPending,
    refresh: contactTypesRefresh,
  },
] = await Promise.all([
  useHttpLazyFetch(sellerProfileAPI.getProfileViewContact, {
    queryParams: { views: 'contacts' },
  }),
  useHttpLazyFetch(sellerProfileAPI.getContactsMetadata),
])

const pending = computed(
  () => contactsPending.value || contactTypesPending.value,
)
const error = computed(() => contactsError.value || contactTypesError.value)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const isSubmitting = ref(false)
const additionalContactTypeOptions = computed(() =>
  contactTypes.value?.additionalContactTypes.map(({ label, code }) => ({
    label: label || '',
    value: code,
  })),
)
const additionalContacts = computed(() =>
  contacts.value?.additionalContacts.filter((email) => !!email),
)

const initialValues = { email: '', type: '' } as FormContactValues

function refresh() {
  contactsRefresh()
  contactTypesRefresh()
  setFormValues(FORM_NAME, initialValues)
}

const validate = makeValidate<FormContactValues>({
  email: [
    required(i18n(translations.emailRequired)),
    matchingRegExp(REGEXP_VALID_EMAIL, i18n(translations.emailInvalid)),
  ],
  type: [required(i18n(translations.fieldIsRequired))],
})

async function handleSubmit(additionalContact: FormContactValues) {
  isSubmitting.value = true
  try {
    await $httpFetch(sellerProfileAPI.updateProfile, {
      body: { additionalContact },
    })
    refresh()
    openSuccessToast({ title: i18n(translations.toastAddSuccessContent) })
  } catch {
    openErrorToast({ title: i18n(translations.toastErrorContent) })
  } finally {
    isSubmitting.value = false
  }
}

async function handleDeleteContact({ type }: GetProfileViewContactsItem) {
  isSubmitting.value = true
  try {
    await $httpFetch(sellerProfileAPI.updateProfile, {
      body: {
        additionalContact: {
          type: type.code,
          email: '',
        },
      },
    })
    refresh()
    openSuccessToast({ title: i18n(translations.toastDeleteSuccessContent) })
  } catch {
    openErrorToast({ title: i18n(translations.toastErrorContent) })
  } finally {
    isSubmitting.value = false
  }
}
</script>
